"use client";

import { useBlockPage } from "@pulsex/hooks";
import Block from "./Block";
import pulseConfig from "@/pulsex.config";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useState, Suspense } from "react";
import Spinner from "./Spinner";
import dynamic from "next/dynamic";
import "@pulsex/builder/global.css";

// Dynamically import the editor components
const EditablePage = dynamic(() => import("@pulsex/builder").then(mod => mod.EditablePage), {
    ssr: false,
    loading: () => <Spinner />
});

const EditableBlock = dynamic(() => import("@pulsex/builder").then(mod => mod.EditableBlock), {
    ssr: false,
});

interface DynamicPageProps {
    isMobile: boolean;
    isEditorMode?: boolean; // Optional with a default of false
}

export default function DynamicPage({ isMobile, isEditorMode = false }: DynamicPageProps) {
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const fullPath = `${pathname}${
        searchParams.toString() ? `?${searchParams.toString()}` : ""
    }`;
    const { data, isLoading } = useBlockPage(pulseConfig, fullPath);
    const [isScreenSmall, setIsScreenSmall] = useState(false);
    
    useEffect(() => {
        if (typeof window !== "undefined") {
            const handleResize = () => {
                if (window.innerWidth < 960) {
                    setIsScreenSmall(true);
                } else {
                    setIsScreenSmall(false);
                }
            };

            window.addEventListener("resize", handleResize);
            handleResize();

            return () => {
                window.removeEventListener("resize", handleResize);
            };
        }
    }, []);

    useEffect(() => {
        // enforce dark mode in videos pages
        if (
            [
                "/newsfeed",
                "/videos",
                "/videofeatures",
                "/explainers",
                "/tv",
                "/digitalseries",
                "/live",
            ].includes(pathname?.toLocaleLowerCase()) ||
            pathname?.toLocaleLowerCase()?.includes("videos")
        ) {
            document.documentElement.classList.add("dark");
        }
    }, [pathname]);
    // Render blocks based on whether editor mode is enabled
    const renderBlocks = () => {
        if (!data?.blocks) return null;
        if (isEditorMode) {
            // Editor mode - wrap with editable components
            return (
                <Suspense fallback={
                    <div className="grid place-items-center w-full h-full pt-10">
                        <Spinner />
                    </div>
                }>
                    <EditablePage config={pulseConfig}>
                        {
                            data.blocks.map((block, i) => (
                                <EditableBlock
                                    key={i}
                                    block={block}
                                    config={pulseConfig}
                                >
                                    <Block
                                        key={i}
                                        block={block}
                                        pagePostId={data?.postId}
                                        isMobile={isMobile || isScreenSmall}
                                    />
                                </EditableBlock>
                            ))
                        }
                    </EditablePage>
                </Suspense>
            );
        } else {
            // Normal mode - render blocks directly
            return data.blocks.map((block, i) => (
                <Block
                    key={i}
                    block={block}
                    pagePostId={data?.postId}
                    isMobile={isMobile || isScreenSmall}
                />
            ));
        }
    };

    return (
        <main className="min-h-screen">
            {isLoading ? (
                // Render skeleton blocks while loading
                <div className="grid place-items-center w-full h-full pt-10">
                    <Spinner />
                </div>
            ) : (
                // Render blocks with or without editor components
                renderBlocks()
            )}
        </main>
    );
}
